import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  useBiometricLayouts,
  useBiometricPresets,
  useBiometricSystems,
  useDeleteBiometricLayout,
  useSaveBiometricLayout,
} from "../../../../api/queries/attendanceQueries";
import { getApiErrorMessage } from "../../../../utils/apiError";
import { formatDateTime } from "../../../../utils/dateTime";
import { EMPLOYEE_MATCH, LAYOUTS } from "./bioConfig";
import LayoutBuilder from "./LayoutBuilder";
import { useCanModify } from "../../../../hooks/usePermissions";

const EMPTY = [];

/** Saved layouts (how each device export is read) + the builder. */
const LayoutsTab = () => {
  const canModify = useCanModify("attendance", { company: true });
  const layoutsQuery = useBiometricLayouts();
  const presetsQuery = useBiometricPresets();
  const remove = useDeleteBiometricLayout();
  const save = useSaveBiometricLayout();
  const [editing, setEditing] = useState(null); // {} = new, layout = edit
  const layouts = layoutsQuery.data || EMPTY;
  const presets = presetsQuery.data || EMPTY;
  const knownSystems = useBiometricSystems().data || EMPTY;
  const systems = [
    ...new Set(
      [
        ...knownSystems.map((s) => s.system),
        ...layouts.map((l) => l.system),
      ].filter(Boolean),
    ),
  ];

  if (editing) {
    return (
      <LayoutBuilder
        key={editing.id || "new"}
        layout={editing.id || editing.config ? editing : null}
        presets={presets}
        systems={systems}
        onDone={() => setEditing(null)}
      />
    );
  }

  const makeDefault = (l) =>
    save.mutate(
      { id: l.id, isDefault: true },
      {
        onSuccess: () => toast.success(`${l.name} is now the default.`),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not update.")),
      },
    );
  const doDelete = (l) => {
    if (!window.confirm(`Delete layout "${l.name}"?`)) return;
    remove.mutate(l.id, {
      onSuccess: () => toast.success("Layout deleted."),
      onError: (err) =>
        toast.error(getApiErrorMessage(err, "Could not delete.")),
    });
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <p className="fs-085 text-slate-600 mb-0">
          A layout tells the system how to read one device's export. Uploads use
          the default one unless you pick another.
        </p>
        {canModify && (
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={() => setEditing({})}
          >
            <i className="bi bi-plus-lg"></i> New layout
          </button>
        )}
      </div>

      <div className="glass-table-card mb-4">
        <div className="table-responsive">
          <table className="table table-hover mb-0 att-table">
            <thead className="custom-table-header">
              <tr>
                <th className="ps-4 py-3">Name</th>
                <th className="py-3">Device / software</th>
                <th className="py-3">Layout</th>
                <th className="py-3">Match by</th>
                <th className="py-3">Updated</th>
                <th className="py-3 pe-4 text-end">Actions</th>
              </tr>
            </thead>
            <tbody className="border-top-0">
              {layouts.map((l) => (
                <tr key={l.id}>
                  <td className="ps-4 fw-semibold">
                    {l.name}
                    {l.isDefault && (
                      <span className="status-pill status-pill--green ms-2">
                        Default
                      </span>
                    )}
                  </td>
                  <td>{l.system || "—"}</td>
                  <td>
                    {LAYOUTS[l.config?.layout]?.label ||
                      l.config?.layout ||
                      "—"}
                  </td>
                  <td>
                    {EMPLOYEE_MATCH[l.config?.employeeMatch] ||
                      l.config?.employeeMatch ||
                      "—"}
                  </td>
                  <td className="text-nowrap">{formatDateTime(l.updatedAt)}</td>
                  <td className="pe-4 text-end text-nowrap">
                    {canModify && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border att-action-btn"
                          onClick={() => setEditing(l)}
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </button>
                        {!l.isDefault && (
                          <button
                            type="button"
                            className="btn btn-sm btn-light border att-action-btn ms-1"
                            onClick={() => makeDefault(l)}
                            disabled={save.isPending}
                          >
                            Make default
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-light border att-action-btn ms-1 text-danger"
                          onClick={() => doDelete(l)}
                          disabled={remove.isPending}
                          aria-label={`Delete ${l.name}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!layoutsQuery.isLoading && layouts.length === 0 && (
                <tr>
                  <td colSpan={6} className="approval-empty">
                    <i className="bi bi-layout-text-window"></i>No layouts yet:
                    uploads use the punch-list preset.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {layoutsQuery.isError && (
          <div className="approval-empty text-danger">
            {getApiErrorMessage(layoutsQuery.error, "Could not load layouts.")}
          </div>
        )}
      </div>

      {presets.length > 0 && (
        <>
          <h2 className="bio-subtitle">Built-in presets</h2>
          <div className="row g-2">
            {presets.map((p) => (
              <div key={p.key} className="col-md-4">
                <div className="att-card h-100">
                  <div className="fw-semibold">{p.name}</div>
                  <div className="fs-075 text-slate-500 mb-2">
                    {p.description}
                  </div>
                  {canModify && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        setEditing({
                          id: null,
                          name: p.name,
                          system: "",
                          config: p.config,
                        })
                      }
                    >
                      Start from this
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default LayoutsTab;
